import { AxiosInstance } from 'axios';
import gql from 'graphql-tag';

import GraphQLClient from '../../adapter/GraphQLClient';
import { BusinessRegionIdBusinessRegion, OrganizationDetailFragment } from '../../generated/graphql';

const getKey = (businessRegion: BusinessRegionIdBusinessRegion | undefined) => {
  switch (businessRegion) {
    case 'japan':
      return 'jp';
      break;
    case 'united_states':
      return 'us';
      break;
    case 'global':
      return 'gl';
      break;
    case undefined:
      return undefined;
      break;
  }
};

export const organizationDetailFragment = gql`
  fragment OrganizationDetail on Organization {
    id
    name
    pointBalance
    features {
      canOpenOfferWhenDisagreed
    }
    businessRegion {
      key
      idBusinessRegion
      timeZone
    }
  }
`;

class OrganizationWebApi {
  private readonly graphqlClient: GraphQLClient;
  private httpClient: AxiosInstance;

  constructor({ graphqlClient, httpClient }: { graphqlClient: GraphQLClient; httpClient: AxiosInstance }) {
    this.graphqlClient = graphqlClient;
    this.httpClient = httpClient;
  }

  public getById = async (organizationId: string): Promise<OrganizationDetailFragment> => {
    const result = await this.graphqlClient.query(
      gql`
        ${organizationDetailFragment}
        query getOrganizationById($id: ID!) {
          organization(id: $id) {
            ...OrganizationDetail
          }
        }
      `,
      { id: organizationId },
    );

    if (!result.organization) {
      throw new OrganizationNotFound({ id: organizationId });
    }

    return result.organization as OrganizationDetailFragment;
  };

  public getAllJoinedOrganizations = async (): Promise<Array<OrganizationDetailFragment>> => {
    const response = await this.httpClient.get('/joined_organizations');
    const jsonArray = await response.data;
    // pointBalance は null 固定（あとから getById() で取得する）
    return jsonArray.map((json: any) => {
      return {
        ...json,
        businessRegion: {
          idBusinessRegion: json.business_region,
          key: getKey(json.business_region),
        },
      };
    });
  };
}

export class OrganizationNotFound extends Error {
  public readonly name = 'ProjectNotFound';
  public readonly message: string;
  public readonly id: string;

  constructor({ id }: { id: string }) {
    super();

    this.id = id;
    this.message = `message.fetch_creations_has_failed`;
  }
}

export class OrganizationFetchingFailed extends Error {
  public readonly name = 'OrganizationFetchingFailed';
  public readonly message: string;

  constructor({
    invitationToken,
    status,
    responseBody,
  }: {
    invitationToken: string;
    status: number;
    responseBody: string;
  }) {
    super();

    this.message = `Fetching the organization (invitationToken: ${invitationToken}) is failed. (${status})\n${responseBody}`;
  }
}

export class OrganizationJoiningFailed extends Error {
  public readonly name = 'OrganizationJoiningFailed';
  public readonly message: string;

  constructor({
    invitationToken,
    status,
    responseBody,
  }: {
    invitationToken: string;
    status: number;
    responseBody: string;
  }) {
    super();

    this.message = `Joining into the organization (invitationToken: ${invitationToken}) is failed. (${status})\n${responseBody}`;
  }
}

export default OrganizationWebApi;
